import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarTipoSolicitud = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código original (llave) por si el usuario lo modifica
  const [tsolcodigoViejo, setTsolcodigoViejo] = useState("")

  // Estado del formulario incluyendo Auto-Aprobación y Mostrar Stock
  const [formData, setFormData] = useState({
    tsolcodigo: "",
    tsoldescripcion: "",
    tsolInEg: "",
    tsolstatus: "A",
    ciaaproautinsol: 0,
    ciamostrarstock: 0,
  })

  // ==========================================================================
  // CONSULTA DEL PARÁMETRO SGA DE LA COMPAÑÍA
  // ==========================================================================
  const { data: paramSGA, isLoading: loadSGA } = useQuery({
    queryKey: ["getParametroSGA_Editar"],
    queryFn: async () => {
      try {
        const response = await api.get("/TipoSolicitud/getParametroSGA")
        return response?.data?.data || { hasSGA: false }
      } catch (error) {
        console.error("Error al cargar parámetro SGA:", error)
        return { hasSGA: false }
      }
    },
  })

  const hasSGA = paramSGA?.hasSGA || false

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla
    if (state) {
      // Normalizar ciamostrarstock: puede venir como number, string ("-1", "0") o null
      const rawMostrarStock = state.ciamostrarstock
      const ciamostrarstockNorm =
        rawMostrarStock === null || rawMostrarStock === undefined || rawMostrarStock === ""
          ? 0
          : Number(rawMostrarStock) === -1
            ? -1
            : 0

      setFormData({
        tsolcodigo: state.tsolcodigo || "",
        tsoldescripcion: state.tsoldescripcion || "",
        tsolInEg: state.tsolInEg || "",
        tsolstatus: state.tsolstatus || "A",
        ciaaproautinsol: state.ciaaproautinsol || 0, // Carga del estado anterior
        ciamostrarstock: ciamostrarstockNorm, // Carga del estado anterior (normalizado)
      })
      setTsolcodigoViejo(state.tsolcodigo || "")
    } else {
      navigate(-1) // Retorno de seguridad si entran directo a la URL sin pasar por la grilla
    }
  }, [state, navigate])

  const {
    mutateAsync: SaveEdicionTipoSolicitud,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingTipoSolicitud"],
    fn: async (data) => {
      const response = await api.post("/TipoSolicitud/updateTipoSolicitud", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1), // Retorna a la grilla tras guardar
  })

  // Manejar cambios en los campos del formulario con limpieza y topes
  const handleInputChange = (field, value) => {
    // Si el campo es switch numérico, pasarlo directo
    if (field === "ciaaproautinsol" || field === "ciamostrarstock") {
      setFormData((prev) => ({ ...prev, [field]: value }))
      return
    }

    // Los campos de estado y naturaleza respetan su camelCase/texto original.
    let finalValue = field === "tsolstatus" || field === "tsolInEg" ? value : value.toUpperCase()

    if (field === "tsolcodigo") finalValue = finalValue.slice(0, 3)
    else if (field === "tsoldescripcion") finalValue = finalValue.slice(0, 60)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend para la edición con llave original
    const payload = {
      tsolcodigoOld: tsolcodigoViejo,
      tsolcodigoNew: formData.tsolcodigo,
      tsoldescripcion: formData.tsoldescripcion,
      tsolInEg: formData.tsolInEg,
      tsolstatus: formData.tsolstatus,
      ciaaproautinsol: formData.ciaaproautinsol,
      ciamostrarstock: formData.ciamostrarstock,
    }

    try {
      if (!payload.tsolcodigoNew.trim()) return showWarning("El Código del Tipo de Solicitud es obligatorio")
      if (!payload.tsoldescripcion.trim()) return showWarning("La Descripción es obligatoria")

      await SaveEdicionTipoSolicitud(payload)
    } catch (error) {
      console.error("Error al editar el Tipo de Solicitud:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú (TOP bar filtrada solo para GRABAR)
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar/Actualizar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || loadSGA}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Tipo de Solicitud: {tsolcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion || loadSGA} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Tipo de Solicitud
            </Typography>
            <Grid container spacing={3} alignItems="center">
              {/* CÓDIGO */}
              <Grid item xs={12} sm={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.tsolcodigo}
                  onChange={(e) => handleInputChange("tsolcodigo", e.target.value)}
                  error={isError && !formData.tsolcodigo}
                  inputProps={{ maxLength: 3 }}
                  disabled
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.tsoldescripcion}
                  onChange={(e) => handleInputChange("tsoldescripcion", e.target.value)}
                  error={isError && !formData.tsoldescripcion}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>

              {/* NATURALEZA (COMBO AMPLIADO) */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Naturaleza *"
                  value={formData.tsolInEg}
                  onChange={(e) => handleInputChange("tsolInEg", e.target.value)}
                  error={isError && !formData.tsolInEg}
                >
                  <MenuItem value="SOLEGRBOD">SOLICITUD DE TRANSFERENCIA ENTRE BODEGAS</MenuItem>
                  <MenuItem value="SOLINGBOD">SOLICITUD DE TRANSFERENCIA ENTRE BODEGAS ING</MenuItem>
                  <MenuItem value="SOLEGRLOC">SOLICITUD DE TRASNFERENCIA ENTRE LOCALIDAD</MenuItem>
                  <MenuItem value="SOLINGLOC">SOLICITUD DE TRASNFERENCIA ENTRE LOCALIDAD ING</MenuItem>
                  <MenuItem value="SOLEGRPRES">SOLICITUD DE PRESTAMOS A CLIENTES</MenuItem>
                  <MenuItem value="SOLINGDPRE">SOLICITUD DE DEVOLUCION DE PRESTAMOS A CLIENTES</MenuItem>
                  <MenuItem value="SOLINGAJU">SOLICITUD DE INGRESO POR AJUSTE</MenuItem>
                  <MenuItem value="SOLEGRAJU">SOLICITUD DE EGRESO POR AJUSTE</MenuItem>
                  {/* OPCIONES CONDICIONALES SGA */}
                  {hasSGA && <MenuItem value="SOLINGGSGA">SOLICITUD DE INGRESO GESTION SGA</MenuItem>}
                  {hasSGA && <MenuItem value="SOLEGRGSGA">SOLICITUD DE EGRESO GESTION SGA</MenuItem>}
                </TextField>
              </Grid>

              {/* ESTADO (ACTIVO/INACTIVO) */}
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.tsolstatus}
                  onChange={(e) => handleInputChange("tsolstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>

              {/* NUEVO SWITCH: AUTO-APROBACIÓN */}
              <Grid item xs={12} sm={12}>
                <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f8f9fa", p: 1, borderRadius: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.ciaaproautinsol === 1}
                        onChange={(e) => handleInputChange("ciaaproautinsol", e.target.checked ? 1 : 0)}
                        color="secondary"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        Permitir Auto-Aprobación para este tipo de solicitud
                      </Typography>
                    }
                  />
                </Box>
              </Grid>

              {/* NUEVO SWITCH: MOSTRAR SOLO PRODUCTOS CON STOCK (ciamostrarstock) */}
              <Grid item xs={12} sm={12}>
                <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f8f9fa", p: 1, borderRadius: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.ciamostrarstock === -1}
                        onChange={(e) => handleInputChange("ciamostrarstock", e.target.checked ? -1 : 0)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        Mostrar solo productos con stock
                      </Typography>
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarTipoSolicitud
